import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, RefreshCcw, Download, Search, Lock, Loader2,
  Mail, Phone, Calendar, Users, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "deleg_admin_pw";

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function PasswordGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/auth`, {}, { headers: { "X-Admin-Password": pw } });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onSuccess(pw);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Mot de passe incorrect.");
      else toast.error("Erreur de connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A2E4A] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md bg-white rounded-sm p-8 sm:p-10 shadow-2xl"
      >
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-[#E8611A] text-white font-display font-extrabold">
            DA
          </span>
          <span className="font-display font-bold text-[#1A2E4A] text-lg">Deleg Admin</span>
        </div>
        <h1 className="font-display font-extrabold text-[#1A2E4A] text-2xl sm:text-3xl">
          Espace administrateur
        </h1>
        <p className="text-[#8A8A8A] text-sm mt-2 mb-7">
          Saisissez le mot de passe pour consulter les leads.
        </p>
        <label className="block text-[#2D2D2D] text-sm font-semibold mb-1.5">Mot de passe</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
            className="w-full pl-10 pr-3.5 py-3 rounded-sm border border-black/10 bg-white text-[#2D2D2D] text-sm focus:border-[#E8611A] focus:outline-none transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !pw}
          data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-sm bg-[#E8611A] text-white font-semibold hover:bg-[#E8611A]/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Connexion…</> : "Se connecter"}
        </button>
        <a
          href="/"
          className="mt-4 block text-center text-[#8A8A8A] text-xs hover:text-[#1A2E4A] transition-colors"
        >
          ← Retour au site
        </a>
      </form>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-black/5 rounded-sm p-5 flex items-center gap-4">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-[#E8611A]/10 text-[#E8611A]">
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </span>
      <div>
        <div className="text-[#8A8A8A] text-xs uppercase tracking-wider font-medium">{label}</div>
        <div className="font-display font-extrabold text-[#1A2E4A] text-2xl">{value}</div>
      </div>
    </div>
  );
}

function LeadsDashboard({ password, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_formule: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [exporting, setExporting] = useState(false);

  const headers = { "X-Admin-Password": password };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/leads/stats`, { headers }),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée — reconnectez-vous.");
        onLogout();
      } else {
        toast.error("Impossible de charger les leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      [l.nom, l.email, l.telephone, l.metier, l.formule, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, query]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/leads/export.csv`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `leads-delegadmin-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch {
      toast.error("Erreur pendant l'export.");
    } finally {
      setExporting(false);
    }
  };

  const top = Object.entries(stats.by_formule || {}).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="min-h-screen bg-[#F5F0EB]" data-testid="admin-dashboard">
      <header className="bg-[#1A2E4A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#E8611A] font-display font-extrabold text-sm">DA</span>
            <div>
              <div className="font-display font-bold text-base sm:text-lg">Deleg Admin · Console</div>
              <div className="text-white/55 text-xs">Tableau de bord des leads</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              className="hidden sm:inline-flex items-center px-3 py-2 rounded-sm text-sm text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              ← Voir le site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/20 text-sm text-white hover:bg-white/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Users} label="Leads total" value={stats.total} />
          <StatCard
            icon={TrendingUp}
            label="Formule la plus demandée"
            value={top ? `${top[0]} (${top[1]})` : "—"}
          />
          <StatCard
            icon={Calendar}
            label="Dernier reçu"
            value={leads[0] ? formatDate(leads[0].created_at).split(" ")[0] : "—"}
          />
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-black/5 rounded-sm p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher (nom, email, métier…)"
              data-testid="admin-search"
              className="w-full pl-10 pr-3 py-2.5 rounded-sm border border-black/10 bg-white text-sm focus:border-[#E8611A] focus:outline-none transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm border border-black/10 text-sm font-medium text-[#2D2D2D] hover:bg-[#F5F0EB] transition-colors disabled:opacity-50"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </button>
            <button
              onClick={exportCsv}
              disabled={exporting || leads.length === 0}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-sm bg-[#E8611A] text-white text-sm font-semibold hover:bg-[#E8611A]/90 disabled:opacity-50 transition-colors"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-black/5 rounded-sm overflow-hidden" data-testid="admin-leads-table">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-[#8A8A8A]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-display font-bold text-[#1A2E4A] text-lg">Aucun lead</p>
              <p className="text-[#8A8A8A] text-sm mt-1">
                {query ? "Aucun résultat pour cette recherche." : "Les nouveaux contacts s'afficheront ici."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F0EB] text-[#1A2E4A]">
                  <tr>
                    <Th>Date</Th>
                    <Th>Nom</Th>
                    <Th>Contact</Th>
                    <Th>Métier</Th>
                    <Th>Formule</Th>
                    <Th>Message</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr
                      key={l.id}
                      data-testid={`admin-lead-row-${i}`}
                      className="border-t border-black/5 hover:bg-[#F5F0EB]/40 transition-colors"
                    >
                      <td className="px-4 py-4 align-top text-[#2D2D2D] whitespace-nowrap tabular-nums">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="px-4 py-4 align-top font-semibold text-[#1A2E4A]">{l.nom}</td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex items-center gap-1.5 text-[#2D2D2D]">
                          <Mail className="w-3.5 h-3.5 text-[#8A8A8A]" />
                          <a href={`mailto:${l.email}`} className="hover:text-[#E8611A]">{l.email}</a>
                        </div>
                        {l.telephone && (
                          <div className="flex items-center gap-1.5 text-[#8A8A8A] text-xs mt-1">
                            <Phone className="w-3 h-3" />
                            <a href={`tel:${l.telephone.replace(/\s/g, "")}`} className="hover:text-[#E8611A]">{l.telephone}</a>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-[#2D2D2D]">{l.metier || "—"}</td>
                      <td className="px-4 py-4 align-top">
                        {l.formule ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-sm bg-[#E8611A]/10 text-[#E8611A] text-xs font-semibold">
                            {l.formule}
                          </span>
                        ) : <span className="text-[#8A8A8A]">—</span>}
                      </td>
                      <td className="px-4 py-4 align-top text-[#2D2D2D] max-w-md">
                        <p className="line-clamp-3">{l.message || "—"}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && (
          <p className="text-[#8A8A8A] text-xs mt-3 text-right">
            {filtered.length} / {leads.length} lead{leads.length > 1 ? "s" : ""}
          </p>
        )}
      </main>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
      {children}
    </th>
  );
}

export default function Admin() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(!!password);

  useEffect(() => {
    if (!password) {
      setChecking(false);
      return;
    }
    axios
      .post(`${API}/admin/auth`, {}, { headers: { "X-Admin-Password": password } })
      .then(() => setVerified(true))
      .catch(() => {
        sessionStorage.removeItem(STORAGE_KEY);
        setPassword("");
      })
      .finally(() => setChecking(false));
  }, [password]);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setVerified(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#1A2E4A] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
      </div>
    );
  }

  if (!verified) {
    return (
      <PasswordGate
        onSuccess={(pw) => {
          setPassword(pw);
          setVerified(true);
        }}
      />
    );
  }

  return <LeadsDashboard password={password} onLogout={logout} />;
}
